import { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import { CallEnd } from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getCurrentLanguage } from '../../utils/navigation';

const VideoCall = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lang = getCurrentLanguage();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const initRef = useRef(false);

  const handleLeave = useCallback(() => {
    if (apiRef.current) {
      try { apiRef.current.dispose(); } catch (e) { /* ignore */ }
      apiRef.current = null;
    }
    const role = user?.role || 'student';
    navigate(`/${lang}/${role}/dashboard`);
  }, [user, lang, navigate]);

  useEffect(() => {
    // Prevent double init
    if (initRef.current || !roomId || !containerRef.current) return;
    initRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;

    script.onload = () => {
      if (!window.JitsiMeetExternalAPI || apiRef.current) return;

      // Clear container
      containerRef.current.innerHTML = '';

      try {
        apiRef.current = new window.JitsiMeetExternalAPI('meet.jit.si', {
          roomName: `Lingoply-${roomId}`,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            lang: lang === 'ar' ? 'ar' : lang === 'fr' ? 'fr' : 'en',
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            SHOW_BRAND_WATERMARK: false,
            TOOLBAR_ALWAYS_VISIBLE: true,
            MOBILE_APP_PROMO: false,
          },
          userInfo: {
            displayName: user?.displayName || 'User',
            email: user?.email || '',
          }
        });

        apiRef.current.addListener('readyToClose', handleLeave);
      } catch (err) {
        console.error('Jitsi error:', err);
      }
    };

    document.head.appendChild(script);

    return () => {
      if (apiRef.current) {
        try { apiRef.current.dispose(); } catch (e) { /* ignore */ }
        apiRef.current = null;
      }
      initRef.current = false;
    };
  }, [roomId]);

  if (!roomId) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">No room ID</Typography>
          <Button onClick={handleLeave} sx={{ mt: 2 }}>Go Back</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Minimal top bar */}
      <Box sx={{
        px: 2, py: 0.5, bgcolor: '#1a1a1a',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        minHeight: 44, flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src="/images/lingoply-logo.jpg" alt=""
            sx={{ width: 26, height: 26, borderRadius: '50%' }} />
          <Typography variant="body2" fontWeight={700} color="#F5A623">
            Lingoply Live
          </Typography>
        </Box>
        <Button
          size="small" variant="contained" color="error"
          startIcon={<CallEnd sx={{ fontSize: 18 }} />}
          onClick={handleLeave}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 0.5 }}
        >
          Leave
        </Button>
      </Box>

      {/* Jitsi container — single div, never duplicated */}
      <Box ref={containerRef} sx={{ flex: 1, width: '100%', overflow: 'hidden' }} />
    </Box>
  );
};

export default VideoCall;
